/**
 * CAT Form Component (Multi-Step Wizard)
 *
 * Form for creating and editing CAT records with step-by-step navigation.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Typography,
  Divider,
  Autocomplete,
  SelectChangeEvent,
  Stepper,
  Step,
  StepLabel,
  Button,
  IconButton,
  InputAdornment,
  CircularProgress,
  FormHelperText
} from '@mui/material';
import {
  NavigateNext as NextIcon,
  NavigateBefore as BackIcon,
  Search as SearchIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import { CompanySelectionModal, CompanyInfoBox } from '../../../components/forms';
import { useCompanySelection } from '../../../hooks/useCompanySelection';
import { useCEPLookup } from '../../../hooks/useCEPLookup';
import ESocialReferenceAutocomplete from '../../companies/components/esocial/forms/ESocialReferenceAutocomplete';
import { departmentService } from '../../../services/departmentService';
import { occupationService } from '../../../services/occupationService';
import { useListaPermitida } from '../../../authorization/useListaPermitida';
import { CatPrestadoresSection } from './CatPrestadoresSection';
import {
  PrestadoresGravados,
  ROTULO_CLINICA_FORA_DA_LISTA,
  ROTULO_RESPONSAVEL_FORA_DA_LISTA,
  gravadoDaClinica,
  gravadoDoResponsavel,
  nomeDoPrestador,
  useOpcoesDePrestadores
} from './catPrestadores';
import {
  CatFormData,
  CatReportType,
  AccidentType,
  AccidentLocationType,
  CatIssuer,
  Laterality,
  CAT_REPORT_TYPE_LABELS,
  ACCIDENT_TYPE_LABELS,
  ACCIDENT_LOCATION_TYPE_LABELS,
  CAT_ISSUER_LABELS,
  LATERALITY_LABELS
} from '../../../types/cat';

interface CatFormEmployee {
  id: number;
  name: string;
  cpf: string;
  departmentId?: number | null;
  occupationId?: number | null;
}

interface OpcaoDeCadastro {
  id: number;
  name: string;
  departmentId?: number;
  companyId?: number;
}

interface CatFormProps {
  formData: CatFormData;
  onChange: (data: Partial<CatFormData>) => void;
  errors?: Record<string, string>;
  readOnly?: boolean;
  // External data for selects
  companies?: Array<{ id: number; tradeName: string; legalName: string }>;
  employees?: Array<CatFormEmployee>;
  cidCodes?: Array<{ id: number; code: string; description: string }>;
  companyAddress?: {
    address?: string;
    addressNumber?: string;
    neighborhood?: string;
    zipCode?: string;
    city?: string;
    state?: string;
    ibgeCode?: string;
  };
  onCompanyChange?: (companyId: number) => void;
  onStepChange?: (step: number, isLastStep: boolean) => void;
  /** Cadastro novo ou edição: muda o rótulo do botão da última etapa e liga a sugestão de clínica. */
  mode?: 'create' | 'edit';
  /** Mesmo salvamento do botão do cabeçalho; o botão da última etapa o chama. */
  onSubmit?: () => void;
  saving?: boolean;
  /** Clínica e responsáveis já gravados na CAT: nome de quem saiu da lista de opções. */
  savedProviders?: PrestadoresGravados;
}

const BRAZILIAN_STATES = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA',
  'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN',
  'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
];

const STEPS = [
  'Funcionário e Acidente',
  'Local do Acidente',
  'Lesão e Diagnóstico',
  'Atendimento e Emissão',
  'Complementos'
];

// Fields required for each step validation
const STEP_FIELDS: Record<number, (keyof CatFormData)[]> = {
  0: ['companyId', 'employeeId', 'reportType', 'accidentType', 'issuer', 'accidentDate', 'generatingSituation'],
  1: ['accidentLocationType'],
  2: ['codParteAting', 'codNatLesao', 'codAgntCausador'],
  3: [],
  4: []
};

// As páginas gravam o erro de alguns campos sob outro nome (validateForm de criação e edição)
const ERROR_KEY_OF_FIELD: Partial<Record<keyof CatFormData, string>> = {
  codParteAting: 'affectedBodyPart',
  codNatLesao: 'injuryNature',
  codAgntCausador: 'causingAgent'
};

/** 'AAAA-MM-DD' (ou com hora) para 'DD/MM/AAAA', sem passar por fuso horário. */
const formatIsoDate = (iso: string): string => {
  const [year, month, day] = iso.slice(0, 10).split('-');
  return `${day}/${month}/${year}`;
};

export const CatForm: React.FC<CatFormProps> = ({
  formData,
  onChange,
  errors = {},
  readOnly = false,
  companies = [],
  employees = [],
  cidCodes = [],
  companyAddress,
  onCompanyChange,
  onStepChange,
  mode = 'create',
  onSubmit,
  saving = false,
  savedProviders
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const furthestStep = useRef(0);
  furthestStep.current = Math.max(furthestStep.current, activeStep);
  const companyIdAtual = formData.companyId ? Number(formData.companyId) : undefined;
  const departmentIdAtual = formData.departmentId ? Number(formData.departmentId) : undefined;

  /**
   * Setor e função vêm por `useListaPermitida`: a porta desta tela cobra `cat.create` ou
   * `cat.update`, e as duas listas cobram `department.read` e `occupation.read`. Quem tem uma e
   * não a outra abriria o formulário com o campo vazio, sem saber por quê — o gancho pergunta
   * antes de pedir e devolve o motivo em `bloqueio`.
   *
   * O id chega do servidor como texto e vira número aqui. Cada item guarda de quem é (empresa
   * ou setor), para não mostrar opções da empresa ou do setor anterior enquanto a nova lista
   * não chegou.
   */
  const carregarSetores = useCallback(async (): Promise<OpcaoDeCadastro[]> => {
    if (companyIdAtual === undefined) return [];
    const lista = await departmentService.getDepartmentsByCompanyId(String(companyIdAtual));
    return (lista ?? []).map((d) => ({ id: Number(d.id), name: d.name, companyId: companyIdAtual }));
  }, [companyIdAtual]);
  const setores = useListaPermitida<OpcaoDeCadastro>('department.read', 'Setores', carregarSetores, {
    habilitado: companyIdAtual !== undefined,
  });

  const carregarFuncoes = useCallback(async (): Promise<OpcaoDeCadastro[]> => {
    if (departmentIdAtual === undefined) return [];
    const resposta = await occupationService.getOccupations({
      limit: 1000,
      filters: { companyId: companyIdAtual, departmentId: departmentIdAtual },
    });
    return resposta.data.map((o) => ({
      id: Number(o.id),
      name: o.name,
      departmentId: Number(o.departmentId),
    }));
  }, [companyIdAtual, departmentIdAtual]);
  const funcoes = useListaPermitida<OpcaoDeCadastro>('occupation.read', 'Funções', carregarFuncoes, {
    habilitado: departmentIdAtual !== undefined,
  });

  const prestadores = useOpcoesDePrestadores(
    companyIdAtual,
    mode === 'edit' ? 'cat.update' : 'cat.create'
  );

  const departments = setores.itens.filter((d) => d.companyId === companyIdAtual);
  const occupations = funcoes.itens.filter((o) => o.departmentId === departmentIdAtual);
  const { loading: cepLoading, lookupCEP } = useCEPLookup();
  const {
    modalOpen,
    selectedCompany,
    showInfoBox,
    openModal,
    handleSelect: handleCompanySelect,
  } = useCompanySelection({ isEditMode: readOnly, currentCompanyId: String(formData.companyId ?? '') });

  // Auto-fill company when selection changes
  useEffect(() => {
    if (selectedCompany && selectedCompany.id) {
      const currentValue = String(formData.companyId || '');
      if (currentValue !== selectedCompany.id) {
        onChange({ companyId: Number(selectedCompany.id) });
        if (onCompanyChange) {
          onCompanyChange(Number(selectedCompany.id));
        }
      }
    }
  }, [selectedCompany, formData.companyId, onChange, onCompanyChange]);

  const handleChange = (field: keyof CatFormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string | number>
  ) => {
    const value = event.target.value;
    onChange({ [field]: value });
  };

  const handleSwitchChange = (field: keyof CatFormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    onChange({ [field]: event.target.checked });
  };

  const handleEmployeeSelect = (_event: React.SyntheticEvent, value: CatFormEmployee | null) => {
    onChange({
      employeeId: value?.id || '',
      departmentId: value?.departmentId ? Number(value.departmentId) : '',
      occupationId: value?.occupationId ? Number(value.occupationId) : '',
    });
  };

  const handleDepartmentChange = (event: SelectChangeEvent<string | number>) => {
    const departmentId = event.target.value === '' ? '' : Number(event.target.value);
    const funcaoAtual = occupations.find((o) => o.id === Number(formData.occupationId));
    const funcaoContinuaNoSetor = !!funcaoAtual && funcaoAtual.departmentId === departmentId;
    onChange({
      departmentId,
      occupationId: funcaoContinuaNoSetor ? formData.occupationId : '',
    });
  };

  const handleOccupationChange = (event: SelectChangeEvent<string | number>) => {
    onChange({ occupationId: event.target.value === '' ? '' : Number(event.target.value) });
  };

  const handleCidCodeSelect = (_event: React.SyntheticEvent, value: { id: number; code: string; description: string } | null) => {
    onChange({ cidCodeId: value?.id || '' });
  };

  const handleLocationTypeChange = (event: SelectChangeEvent<string | number>) => {
    const value = event.target.value as AccidentLocationType;

    if (value === AccidentLocationType.EMPLOYER_ESTABLISHMENT && companyAddress) {
      onChange({
        accidentLocationType: value,
        streetName: companyAddress.address ?? '',
        streetNumber: companyAddress.addressNumber ?? '',
        neighborhood: companyAddress.neighborhood ?? '',
        zipCode: companyAddress.zipCode ?? '',
        cityCode: companyAddress.ibgeCode ?? '',
        state: companyAddress.state ?? '',
      });
    } else {
      onChange({
        accidentLocationType: value,
        streetType: '',
        streetName: '',
        streetNumber: '',
        complement: '',
        neighborhood: '',
        zipCode: '',
        cityCode: '',
        state: '',
      });
    }
  };

  const handleCEPLookup = async () => {
    const cep = formData.zipCode ?? '';
    if (cep.length !== 8) return;

    const data = await lookupCEP(cep);
    if (data) {
      onChange({
        streetName: data.address || formData.streetName,
        neighborhood: data.neighborhood || formData.neighborhood,
        cityCode: data.ibgeCode || formData.cityCode,
        state: data.state || formData.state,
      });
    }
  };

  const isEmployerEstablishment = formData.accidentLocationType === AccidentLocationType.EMPLOYER_ESTABLISHMENT;

  const selectedEmployee = employees.find(e => e.id === formData.employeeId);
  const selectedCidCode = cidCodes.find(c => c.id === formData.cidCodeId);
  // Só mostra o valor quando a opção já carregou, para o Select não receber valor fora da lista
  const departmentValue = departments.some(d => d.id === Number(formData.departmentId))
    ? Number(formData.departmentId)
    : '';
  const occupationValue = occupations.some(o => o.id === Number(formData.occupationId))
    ? Number(formData.occupationId)
    : '';

  const hasStepErrors = (step: number): boolean => {
    const fields = STEP_FIELDS[step] ?? [];
    return fields.some(field => {
      const errorKey = ERROR_KEY_OF_FIELD[field];
      // A página limpa o erro pela chave do campo; o erro sob outro nome só vale enquanto o campo está vazio
      return !!errors[field] || (!!errorKey && !!errors[errorKey] && !formData[field]);
    });
  };

  // Ao tentar salvar e a validação da página recusar, volta à primeira etapa com pendência
  const submitAttempted = useRef(false);
  const handleSubmitClick = () => {
    submitAttempted.current = true;
    onSubmit?.();
  };
  useEffect(() => {
    if (!submitAttempted.current) return;
    submitAttempted.current = false;
    const firstStepWithErrors = STEPS.findIndex((_, index) => hasStepErrors(index));
    if (firstStepWithErrors >= 0 && firstStepWithErrors !== activeStep) {
      setActiveStep(firstStepWithErrors);
      onStepChange?.(firstStepWithErrors, firstStepWithErrors === STEPS.length - 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors]);

  const handleNext = (e?: React.MouseEvent<HTMLButtonElement>) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (activeStep < STEPS.length - 1) {
      const nextStep = activeStep + 1;
      setActiveStep(nextStep);
      onStepChange?.(nextStep, nextStep === STEPS.length - 1);
    }
  };

  const handleBack = (e?: React.MouseEvent<HTMLButtonElement>) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (activeStep > 0) {
      const prevStep = activeStep - 1;
      setActiveStep(prevStep);
      onStepChange?.(prevStep, false);
    }
  };

  const handleStepClick = (step: number) => {
    if (readOnly || step <= activeStep) {
      setActiveStep(step);
      onStepChange?.(step, step === STEPS.length - 1);
    }
  };

  // Step 0: Funcionario e Acidente
  const renderEmployeeAndAccident = () => (
    <>
      {/* Employee Data */}
      <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
        Dados do Funcionário
      </Typography>
      <Divider sx={{ mb: 2 }} />
      <Grid container spacing={2}>
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <Autocomplete
            options={employees}
            getOptionLabel={(option) => `${option.name} (CPF: ${option.cpf})`}
            value={selectedEmployee || null}
            onChange={handleEmployeeSelect}
            disabled={readOnly || !formData.companyId}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Funcionário *"
                error={!!errors.employeeId}
                helperText={errors.employeeId || (!formData.companyId ? 'Selecione uma empresa primeiro' : '')}
              />
            )}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 3
          }}>
          <FormControl fullWidth error={!!errors.departmentId}>
            <InputLabel id="cat-department-label">Setor</InputLabel>
            <Select
              labelId="cat-department-label"
              value={departmentValue}
              onChange={handleDepartmentChange}
              label="Setor"
              disabled={readOnly || !formData.companyId}
            >
              <MenuItem value="">-</MenuItem>
              {departments.map((department) => (
                <MenuItem key={department.id} value={department.id}>
                  {department.name}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {errors.departmentId || setores.bloqueio || setores.erro || 'Setor em que o trabalhador estava na data da CAT. Vem do cadastro e pode ser trocado.'}
            </FormHelperText>
          </FormControl>
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 3
          }}>
          <FormControl fullWidth error={!!errors.occupationId}>
            <InputLabel id="cat-occupation-label">Função</InputLabel>
            <Select
              labelId="cat-occupation-label"
              value={occupationValue}
              onChange={handleOccupationChange}
              label="Função"
              disabled={readOnly || !formData.departmentId}
            >
              <MenuItem value="">-</MenuItem>
              {occupations.map((occupation) => (
                <MenuItem key={occupation.id} value={occupation.id}>
                  {occupation.name}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {errors.occupationId || funcoes.bloqueio || funcoes.erro || (!formData.departmentId ? 'Escolha o setor primeiro' : '')}
            </FormHelperText>
          </FormControl>
        </Grid>
      </Grid>

      {/* Accident Data */}
      <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
        Dados do Acidente
      </Typography>
      <Divider sx={{ mb: 2 }} />
      <Grid container spacing={2}>
        <Grid
          size={{
            xs: 12,
            md: 4
          }}>
          <FormControl fullWidth error={!!errors.reportType}>
            <InputLabel>Tipo de CAT *</InputLabel>
            <Select
              value={formData.reportType}
              onChange={handleChange('reportType')}
              label="Tipo de CAT *"
              disabled={readOnly}
            >
              {Object.values(CatReportType).map((type) => (
                <MenuItem key={type} value={type}>
                  {CAT_REPORT_TYPE_LABELS[type]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 4
          }}>
          <FormControl fullWidth error={!!errors.accidentType}>
            <InputLabel>Tipo de Acidente *</InputLabel>
            <Select
              value={formData.accidentType}
              onChange={handleChange('accidentType')}
              label="Tipo de Acidente *"
              disabled={readOnly}
            >
              {Object.values(AccidentType).map((type) => (
                <MenuItem key={type} value={type}>
                  {ACCIDENT_TYPE_LABELS[type]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 4
          }}>
          <FormControl fullWidth error={!!errors.issuer}>
            <InputLabel>Emissor *</InputLabel>
            <Select
              value={formData.issuer}
              onChange={handleChange('issuer')}
              label="Emissor *"
              disabled={readOnly}
            >
              {Object.values(CatIssuer).map((issuer) => (
                <MenuItem key={issuer} value={issuer}>
                  {CAT_ISSUER_LABELS[issuer]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 4
          }}>
          <TextField
            fullWidth
            type="date"
            label="Data do Acidente *"
            value={formData.accidentDate}
            onChange={handleChange('accidentDate')}
            InputLabelProps={{ shrink: true }}
            error={!!errors.accidentDate}
            helperText={errors.accidentDate}
            disabled={readOnly}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 4
          }}>
          <TextField
            fullWidth
            type="time"
            label="Hora do Acidente"
            value={formData.accidentTime ?? ''}
            onChange={handleChange('accidentTime')}
            InputLabelProps={{ shrink: true }}
            disabled={readOnly}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 4
          }}>
          <TextField
            fullWidth
            label="Horas Trabalhadas Antes"
            value={formData.hoursWorkedBefore ?? ''}
            onChange={handleChange('hoursWorkedBefore')}
            placeholder="Ex: 04:30"
            disabled={readOnly}
          />
        </Grid>
        <Grid size={12}>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Situação Geradora *"
            value={formData.generatingSituation}
            onChange={handleChange('generatingSituation')}
            error={!!errors.generatingSituation}
            helperText={errors.generatingSituation}
            placeholder="Descreva a situação que gerou o acidente..."
            disabled={readOnly}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <ESocialReferenceAutocomplete
            tableNumber={15}
            value={formData.codSitGeradora ?? ''}
            onChange={(code) => onChange({ codSitGeradora: code || undefined })}
            label="Situação Geradora - eSocial (Tabela 15)"
            disabled={readOnly}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 4
          }}>
          <FormControl fullWidth>
            <InputLabel>Óbito</InputLabel>
            <Select
              value={formData.isDeathReport}
              onChange={handleChange('isDeathReport')}
              label="Óbito"
              disabled={readOnly}
            >
              <MenuItem value="N">Não</MenuItem>
              <MenuItem value="S">Sim</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </>
  );

  // Step 1: Local do Acidente
  const renderAccidentLocation = () => (
    <>
      <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
        Local do Acidente
      </Typography>
      <Divider sx={{ mb: 2 }} />
      <Grid container spacing={2}>
        <Grid
          size={{
            xs: 12,
            md: 4
          }}>
          <FormControl fullWidth error={!!errors.accidentLocationType}>
            <InputLabel>Tipo de Local *</InputLabel>
            <Select
              value={formData.accidentLocationType}
              onChange={handleLocationTypeChange}
              label="Tipo de Local *"
              disabled={readOnly}
            >
              {Object.values(AccidentLocationType).map((type) => (
                <MenuItem key={type} value={type}>
                  {ACCIDENT_LOCATION_TYPE_LABELS[type]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 4
          }}>
          <TextField
            fullWidth
            label="Tipo de Logradouro"
            value={formData.streetType ?? ''}
            onChange={handleChange('streetType')}
            placeholder="Ex: Rua, Avenida"
            disabled={readOnly || isEmployerEstablishment}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 4
          }}>
          <TextField
            fullWidth
            label="Logradouro"
            value={formData.streetName ?? ''}
            onChange={handleChange('streetName')}
            disabled={readOnly || isEmployerEstablishment}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 2
          }}>
          <TextField
            fullWidth
            label="Número"
            value={formData.streetNumber ?? ''}
            onChange={handleChange('streetNumber')}
            disabled={readOnly || isEmployerEstablishment}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 4
          }}>
          <TextField
            fullWidth
            label="Complemento"
            value={formData.complement ?? ''}
            onChange={handleChange('complement')}
            disabled={readOnly || isEmployerEstablishment}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 3
          }}>
          <TextField
            fullWidth
            label="Bairro"
            value={formData.neighborhood ?? ''}
            onChange={handleChange('neighborhood')}
            disabled={readOnly || isEmployerEstablishment}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 3
          }}>
          <TextField
            fullWidth
            label="CEP"
            value={formData.zipCode ?? ''}
            onChange={handleChange('zipCode')}
            placeholder="00000000"
            inputProps={{ maxLength: 8 }}
            disabled={readOnly || isEmployerEstablishment}
            InputProps={{
              endAdornment: !readOnly && !isEmployerEstablishment ? (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleCEPLookup}
                    disabled={cepLoading || (formData.zipCode ?? '').length !== 8}
                    size="small"
                  >
                    {cepLoading ? <CircularProgress size={20} /> : <SearchIcon />}
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 3
          }}>
          <TextField
            fullWidth
            label="Código IBGE"
            value={formData.cityCode ?? ''}
            onChange={handleChange('cityCode')}
            disabled={readOnly || isEmployerEstablishment}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 2
          }}>
          <FormControl fullWidth>
            <InputLabel>UF</InputLabel>
            <Select
              value={formData.state ?? ''}
              onChange={handleChange('state')}
              label="UF"
              disabled={readOnly || isEmployerEstablishment}
            >
              <MenuItem value="">-</MenuItem>
              {BRAZILIAN_STATES.map((state) => (
                <MenuItem key={state} value={state}>
                  {state}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </>
  );

  // Step 2: Lesao e Diagnostico
  const renderInjuryAndDiagnosis = () => (
    <>
      <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
        Lesão e Diagnóstico
      </Typography>
      <Divider sx={{ mb: 2 }} />
      <Grid container spacing={2}>
        <Grid
          size={{
            xs: 12,
            md: 4
          }}>
          <ESocialReferenceAutocomplete
            tableNumber={13}
            value={formData.codParteAting ?? ''}
            onChange={(code) => onChange({ codParteAting: code || undefined })}
            label="Parte do Corpo Atingida - eSocial (Tabela 13) *"
            required
            error={!!errors.affectedBodyPart && !formData.codParteAting}
            helperText={errors.affectedBodyPart}
            disabled={readOnly}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 4
          }}>
          <FormControl fullWidth>
            <InputLabel>Lateralidade</InputLabel>
            <Select
              value={formData.laterality ?? ''}
              onChange={handleChange('laterality')}
              label="Lateralidade"
              disabled={readOnly}
            >
              <MenuItem value="">-</MenuItem>
              {Object.values(Laterality).map((lat) => (
                <MenuItem key={lat} value={lat}>
                  {LATERALITY_LABELS[lat]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 4
          }}>
          <ESocialReferenceAutocomplete
            tableNumber={17}
            value={formData.codNatLesao ?? ''}
            onChange={(code) => onChange({ codNatLesao: code || undefined })}
            label="Natureza da Lesão - eSocial (Tabela 17) *"
            required
            error={!!errors.injuryNature && !formData.codNatLesao}
            helperText={errors.injuryNature}
            disabled={readOnly}
          />
        </Grid>
        <Grid size={12}>
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Descrição da Lesão"
            value={formData.injuryDescription ?? ''}
            onChange={handleChange('injuryDescription')}
            disabled={readOnly}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <ESocialReferenceAutocomplete
            tableNumber={14}
            value={formData.codAgntCausador ?? ''}
            onChange={(code) => onChange({ codAgntCausador: code || undefined })}
            label="Agente Causador - eSocial (Tabela 14) *"
            required
            error={!!errors.causingAgent && !formData.codAgntCausador}
            helperText={errors.causingAgent}
            disabled={readOnly}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <TextField
            fullWidth
            label="Diagnóstico Provável"
            value={formData.probableDiagnosis ?? ''}
            onChange={handleChange('probableDiagnosis')}
            disabled={readOnly}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <Autocomplete
            options={cidCodes}
            getOptionLabel={(option) => `${option.code} - ${option.description}`}
            value={selectedCidCode || null}
            onChange={handleCidCodeSelect}
            disabled={readOnly}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Código CID-10"
              />
            )}
          />
        </Grid>
      </Grid>
    </>
  );

  // Step 3: Atendimento e Emissao
  const renderTreatmentAndIssuer = () => (
    <>
      {/* Medical Treatment */}
      <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
        Atendimento Médico
      </Typography>
      <Divider sx={{ mb: 2 }} />
      <Grid container spacing={2}>
        <Grid
          size={{
            xs: 12,
            md: 3
          }}>
          <TextField
            fullWidth
            type="date"
            label="Data do Atendimento"
            value={formData.treatmentDate ?? ''}
            onChange={handleChange('treatmentDate')}
            InputLabelProps={{ shrink: true }}
            disabled={readOnly}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 3
          }}>
          <TextField
            fullWidth
            type="time"
            label="Hora do Atendimento"
            value={formData.treatmentTime ?? ''}
            onChange={handleChange('treatmentTime')}
            InputLabelProps={{ shrink: true }}
            disabled={readOnly}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 2
          }}>
          <FormControl fullWidth>
            <InputLabel>Internação</InputLabel>
            <Select
              value={formData.wasHospitalized ?? 'N'}
              onChange={handleChange('wasHospitalized')}
              label="Internação"
              disabled={readOnly}
            >
              <MenuItem value="N">Não</MenuItem>
              <MenuItem value="S">Sim</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 2
          }}>
          <TextField
            fullWidth
            type="number"
            label="Duração (dias)"
            value={formData.treatmentDuration ?? ''}
            onChange={handleChange('treatmentDuration')}
            disabled={readOnly}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 2
          }}>
          <FormControl fullWidth>
            <InputLabel>Afastamento</InputLabel>
            <Select
              value={formData.willBeAbsent ?? 'N'}
              onChange={handleChange('willBeAbsent')}
              label="Afastamento"
              disabled={readOnly}
            >
              <MenuItem value="N">Não</MenuItem>
              <MenuItem value="S">Sim</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* Issuer Professional */}
      <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
        Profissional Emissor
      </Typography>
      <Divider sx={{ mb: 2 }} />
      <Grid container spacing={2}>
        <Grid
          size={{
            xs: 12,
            md: 6
          }}>
          <TextField
            fullWidth
            label="Nome do Profissional"
            value={formData.issuerName ?? ''}
            onChange={handleChange('issuerName')}
            disabled={readOnly}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 2
          }}>
          <TextField
            fullWidth
            label="Órgão de Classe"
            value={formData.classEntity ?? ''}
            onChange={handleChange('classEntity')}
            placeholder="Ex: CRM"
            disabled={readOnly}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 2
          }}>
          <TextField
            fullWidth
            label="Número"
            value={formData.classEntityNumber ?? ''}
            onChange={handleChange('classEntityNumber')}
            disabled={readOnly}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 2
          }}>
          <FormControl fullWidth>
            <InputLabel>UF</InputLabel>
            <Select
              value={formData.classEntityState ?? ''}
              onChange={handleChange('classEntityState')}
              label="UF"
              disabled={readOnly}
            >
              <MenuItem value="">-</MenuItem>
              {BRAZILIAN_STATES.map((state) => (
                <MenuItem key={state} value={state}>
                  {state}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </>
  );

  // Step 4: Complementos (eSocial, Notes)
  const renderComplements = () => (
    <>
      <CatPrestadoresSection
        companyId={companyIdAtual}
        modo={mode}
        estado={prestadores.estado}
        opcoes={prestadores.opcoes}
        valores={{
          clinicId: formData.clinicId ?? '',
          medicalResponsibleId: formData.medicalResponsibleId ?? '',
          technicalResponsibleId: formData.technicalResponsibleId ?? '',
          environmentalResponsibleId: formData.environmentalResponsibleId ?? ''
        }}
        gravados={savedProviders}
        disabled={readOnly}
        onChange={onChange}
      />

      {/* eSocial */}
      <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
        Integração eSocial
      </Typography>
      <Divider sx={{ mb: 2 }} />
      <Grid container spacing={2}>
        <Grid
          size={{
            xs: 12,
            md: 4
          }}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.sendToEsocial}
                onChange={handleSwitchChange('sendToEsocial')}
                disabled={readOnly}
              />
            }
            label="Enviar para eSocial"
          />
        </Grid>
        {/* Ambiente eSocial é determinado automaticamente pelo NODE_ENV do servidor */}
      </Grid>

      {/* Notes */}
      <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
        Observações
      </Typography>
      <Divider sx={{ mb: 2 }} />
      <Grid container spacing={2}>
        <Grid size={12}>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Observações"
            value={formData.notes ?? ''}
            onChange={handleChange('notes')}
            placeholder="Observações adicionais..."
            disabled={readOnly}
          />
        </Grid>
      </Grid>
    </>
  );

  const isLastStep = activeStep === STEPS.length - 1;
  const isFirstStep = activeStep === 0;

  // Etapas com campo obrigatório vazio — as mesmas regras que o salvamento valida
  const stepsWithMissingRequired = STEPS.map((_, index) => index).filter((index) =>
    hasStepErrors(index) || (STEP_FIELDS[index] ?? []).some((field) => !formData[field])
  );
  const stepNameInSentence = (index: number): string =>
    STEPS[index].charAt(0) + STEPS[index].slice(1).toLowerCase();
  const stepProgressText = !isLastStep
    ? null
    : stepsWithMissingRequired.length === 0
      ? 'Tudo o que é obrigatório já está preenchido. Falta só salvar.'
      : `Falta preencher: ${stepsWithMissingRequired.map(stepNameInSentence).join(', ')}.`;

  const companyName =
    companies.find((c) => String(c.id) === String(companyIdAtual))?.tradeName ?? selectedCompany?.name;
  const clinicName = nomeDoPrestador(
    formData.clinicId,
    prestadores.opcoes.clinicas,
    gravadoDaClinica(savedProviders),
    // Só com a lista carregada dá para dizer que o id está fora dela
    prestadores.estado === 'pronto' ? ROTULO_CLINICA_FORA_DA_LISTA : undefined
  );
  const medicalResponsibleName = nomeDoPrestador(
    formData.medicalResponsibleId,
    prestadores.opcoes.responsaveisMedicos,
    gravadoDoResponsavel(savedProviders?.medicalResponsible),
    prestadores.estado === 'pronto' ? ROTULO_RESPONSAVEL_FORA_DA_LISTA : undefined
  );
  const summaryLines = [
    selectedEmployee &&
      `Funcionário: ${[selectedEmployee.name, companyName].filter(Boolean).join(', ')}`,
    formData.accidentDate && `Acidente em ${formatIsoDate(formData.accidentDate)}`,
    clinicName
      ? `Clínica: ${clinicName}${medicalResponsibleName ? ` (${medicalResponsibleName})` : ''}`
      : medicalResponsibleName && `Responsável médico: ${medicalResponsibleName}`
  ].filter((line): line is string => !!line);

  return (
    <Box>
      {showInfoBox && selectedCompany && (
        <CompanyInfoBox
          companyName={selectedCompany.name}
          onEdit={openModal}
        />
      )}

      {/* Stepper */}
      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 2 }}>
        {STEPS.map((label, index) => (
          <Step
            key={label}
            completed={index < activeStep}
            sx={{ cursor: readOnly || index <= activeStep ? 'pointer' : 'default' }}
            onClick={() => handleStepClick(index)}
          >
            <StepLabel
              error={hasStepErrors(index)}
              optional={
                stepsWithMissingRequired.includes(index) &&
                (hasStepErrors(index) || (index !== activeStep && index <= furthestStep.current)) ? (
                  <Typography variant="caption" color="error">
                    Falta preencher
                  </Typography>
                ) : index < activeStep ? (
                  <Typography variant="caption" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                    <CheckIcon aria-hidden sx={{ fontSize: 16 }} />
                    Concluída
                  </Typography>
                ) : undefined
              }
            >
              {label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>
      <Typography sx={{ fontSize: 16, mb: 4, textAlign: 'center' }}>
        Etapa {activeStep + 1} de {STEPS.length}.
        {stepProgressText && ` ${stepProgressText}`}
      </Typography>

      {/* Step Content */}
      <Box sx={{ display: activeStep === 0 ? 'block' : 'none' }}>
        {renderEmployeeAndAccident()}
      </Box>
      <Box sx={{ display: activeStep === 1 ? 'block' : 'none' }}>
        {renderAccidentLocation()}
      </Box>
      <Box sx={{ display: activeStep === 2 ? 'block' : 'none' }}>
        {renderInjuryAndDiagnosis()}
      </Box>
      <Box sx={{ display: activeStep === 3 ? 'block' : 'none' }}>
        {renderTreatmentAndIssuer()}
      </Box>
      <Box sx={{ display: activeStep === 4 ? 'block' : 'none' }}>
        {renderComplements()}
      </Box>

      {/* Navigation Buttons */}
      {!readOnly && (
        <Box sx={{ mt: 4, pt: 2, borderTop: 1, borderColor: 'divider' }}>
          {isLastStep && summaryLines.length > 0 && (
            <Box sx={{ bgcolor: 'grey.100', border: 1, borderColor: 'divider', borderRadius: 1, p: 2, mb: 3 }}>
              <Typography sx={{ fontSize: 16, fontWeight: 600, mb: 1 }}>
                Antes de salvar, confira o resumo:
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 3, fontSize: 16 }}>
                {summaryLines.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </Box>
            </Box>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
            <Button
              variant="text"
              color="inherit"
              startIcon={<BackIcon />}
              onClick={handleBack}
              disabled={isFirstStep}
              sx={{ minHeight: 44, fontSize: 16 }}
            >
              Anterior
            </Button>
            {isLastStep ? (
              <Button
                variant="contained"
                onClick={handleSubmitClick}
                disabled={saving || !onSubmit}
                sx={{ minHeight: 52, fontSize: 16, px: 4 }}
              >
                {saving ? 'Salvando...' : mode === 'edit' ? 'Salvar alterações' : 'Salvar CAT'}
              </Button>
            ) : (
              <Button
                variant="contained"
                endIcon={<NextIcon />}
                onClick={handleNext}
                sx={{ minHeight: 52, fontSize: 16, px: 3 }}
              >
                Próximo: {STEPS[activeStep + 1]}
              </Button>
            )}
          </Box>
          {isLastStep && (
            <Typography sx={{ fontSize: 16, mt: 2, textAlign: 'right' }}>
              Salvar não envia ao eSocial. O envio é um passo separado, na tela da CAT.
            </Typography>
          )}
        </Box>
      )}

      <CompanySelectionModal
        open={modalOpen}
        onSelect={handleCompanySelect}
      />
    </Box>
  );
};

export default CatForm;
